import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OrganiseTransportModal from "@/components/OrganiseTransportModal";
import { Truck, MapPin, ArrowRight, CheckCircle, Package, MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { SkeletonRows } from "@/components/RegisterSkeleton";

const STATUS_META = {
  scheduled: { label: "Scheduled", cls: "bg-amber-50 text-amber-800" },
  in_transit: { label: "In Transit", cls: "bg-blue-50 text-blue-700" },
  delivered: { label: "Delivered", cls: "bg-emerald-50 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-surface-pressed text-ink-secondary" },
};

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

// Normalise legacy status vocab: some records store "in_progress" for the transit leg.
const normStatus = (s) => (s === "in_progress" ? "in_transit" : (s || "scheduled"));

export default function Transport() {
  const { isWriter } = useAuth();
  const [rows, setRows] = useState([]);
  const [projName, setProjName] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [organise, setOrganise] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([api.get("/transportation"), api.get("/projects")]);
      setRows(t.data);
      setProjName(Object.fromEntries(p.data.map((x) => [x.id, x.project_name || x.site_address || x.project_no || "Project"])));
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatusOn = async (row, next) => {
    setBusy(row.id + next); setError("");
    try { await api.put(`/transportation/${row.id}`, { status: next }); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Update failed."); }
    setBusy(null);
  };

  const filtered = useMemo(() => rows.filter((r) => {
    const t = q.toLowerCase();
    const matchQ = !t || [r.asset_no, r.from_location, r.to_location].some((v) => (v || "").toLowerCase().includes(t));
    const matchS = !status || normStatus(r.status) === status;
    return matchQ && matchS;
  }), [rows, q, status]);

  const counts = useMemo(() => {
    const c = { scheduled: 0, in_transit: 0, delivered: 0 };
    rows.forEach((r) => { const s = normStatus(r.status); if (c[s] != null) c[s] += 1; });
    return c;
  }, [rows]);

  return (
    <div className="h-full flex flex-col bg-surface-page p-4" data-testid="transport-page">
      <div className="flex-1 min-h-0 flex flex-col bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-divider-inner shrink-0">
          <div>
            <h1 className="font-heading text-[17px] font-bold tracking-tight text-ink-primary">Transport Board</h1>
            <p className="text-[12px] text-ink-tertiary">Equipment movements across every project &amp; site</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Asset or location…" data-testid="transport-search"
                className="w-60 h-[42px] pl-9 pr-3 bg-white border-[1.5px] border-ink-primary rounded-[9px] text-body focus:outline-none focus:ring-2 focus:ring-equipr-yellow/35" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="transport-status-filter"
              className="h-[42px] px-3 border border-border-control bg-white text-[13.5px] rounded-[9px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none">
              <option value="">All statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            {isWriter && (
              <button onClick={() => setOrganise(true)} data-testid="organise-transport-button"
                className="h-[42px] px-4 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[9px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">
                <Plus size={15} weight="bold" /> Transport
              </button>
            )}
          </div>
        </div>

        {error && <div className="mx-5 mt-3 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]">{error}</div>}

        <div className="flex-1 overflow-auto">
          <table className="w-full text-cell">
            <thead className="sticky top-0 z-10">
              <tr className="bg-surface-sunken text-left border-b border-border">
                {["Asset", "From", "To", "Scheduled", "Status", ""].map((h, i) => (
                  <th key={h || i} className={`px-4 py-2.5 uppercase tracking-[0.1em] text-colhead whitespace-nowrap ${i === 0 ? "font-bold text-ink-secondary" : "font-medium text-ink-muted"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows cols={6} />
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-14 text-center text-ink-tertiary" data-testid="transport-empty">
                  <Package size={28} className="mx-auto mb-2 text-ink-muted" /> {q || status ? "No movements match your filters." : "No transport movements yet."}
                </td></tr>
              ) : filtered.map((r) => {
                const st = STATUS_META[normStatus(r.status)] || STATUS_META.scheduled;
                return (
                  <tr key={r.id} className="border-b border-divider-row hover:bg-accent-wash" data-testid={`transport-row-${r.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-ink-primary">
                        {(r.asset_nos && r.asset_nos.length > 1)
                          ? <span>{r.asset_nos.length} assets <span className="text-[11px] font-normal text-ink-tertiary">({r.asset_nos.join(", ")})</span></span>
                          : (r.asset_no || (r.asset_nos && r.asset_nos[0]) || "—")}
                      </div>
                      {r.project_id && projName[r.project_id] && <div className="text-[11px] text-ink-tertiary">{projName[r.project_id]}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-ink-secondary">{r.from_location || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-ink-primary">
                        <ArrowRight size={13} className="text-ink-tertiary" />
                        {r.to_type === "site" ? <MapPin size={13} className="text-sky-600" /> : <Truck size={13} className="text-ink-tertiary" />}
                        {r.to_location || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-ink-secondary">{fmt(r.scheduled_at)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide rounded-[4px] ${st.cls}`} data-testid={`transport-status-${r.id}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {isWriter && r.status !== "delivered" && r.status !== "cancelled" && (
                        <div className="inline-flex gap-2">
                          {r.status !== "in_transit" && (
                            <button onClick={() => setStatusOn(r, "in_transit")} disabled={busy === r.id + "in_transit"} data-testid={`transport-intransit-${r.id}`}
                              className="h-7 px-2.5 border border-blue-300 text-blue-700 text-xs font-semibold rounded-[7px] hover:bg-blue-50 disabled:opacity-50">In Transit</button>
                          )}
                          <button onClick={() => setStatusOn(r, "delivered")} disabled={busy === r.id + "delivered"} data-testid={`transport-deliver-${r.id}`}
                            className="h-7 px-2.5 bg-equipr-yellow text-equipr-black text-xs font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark inline-flex items-center gap-1 disabled:opacity-50">
                            <CheckCircle size={13} weight="bold" /> Mark Delivered
                          </button>
                        </div>
                      )}
                      {r.status === "delivered" && <span className="text-[11px] text-emerald-600 inline-flex items-center gap-1"><CheckCircle size={13} weight="fill" /> Location updated</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-2 border-t border-divider-inner bg-surface-panel text-meta text-ink-tertiary shrink-0">
          <span data-testid="transport-footer">{loading ? "Loading…" : `${filtered.length} movement${filtered.length === 1 ? "" : "s"}${(q || status) ? ` · filtered from ${rows.length}` : ""}`}</span>
          <span className="flex items-center gap-3">
            <span data-testid="count-scheduled"><b className="text-amber-700">{counts.scheduled}</b> scheduled</span>
            <span data-testid="count-in_transit"><b className="text-blue-700">{counts.in_transit}</b> in transit</span>
            <span data-testid="count-delivered"><b className="text-emerald-700">{counts.delivered}</b> delivered</span>
          </span>
        </div>
      </div>

      {organise && <OrganiseTransportModal onClose={() => setOrganise(false)} onCreated={load} />}
    </div>
  );
}
